import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeId = 'dark' | 'light' | 'midnight' | 'solar' | 'nord';

export type ThemeMeta = {
  id: ThemeId;
  label: string;
  description: string;
  swatch: [string, string]; // accent, bg2 — used for previews
};

export const THEMES: ThemeMeta[] = [
  {
    id: 'dark',
    label: 'Dark',
    description: 'Padrão. Equilibrado pra uso longo.',
    swatch: ['#3b82f6', '#141923'],
  },
  {
    id: 'light',
    label: 'Light',
    description: 'Claro, alto contraste, bom em ambientes iluminados.',
    swatch: ['#2563eb', '#ffffff'],
  },
  {
    id: 'midnight',
    label: 'Midnight',
    description: 'Preto absoluto. Indicado pra OLED.',
    swatch: ['#06b6d4', '#0a0a0a'],
  },
  {
    id: 'solar',
    label: 'Solar',
    description: 'Sépia quente, cansa menos a vista à noite.',
    swatch: ['#b45309', '#faf5ec'],
  },
  {
    id: 'nord',
    label: 'Nord',
    description: 'Paleta nórdica, baixa saturação, foco.',
    swatch: ['#88c0d0', '#3b4252'],
  },
];

const STORAGE_KEY = 'bp:theme';
const DEFAULT_THEME: ThemeId = 'dark';

function readStoredTheme(): ThemeId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && THEMES.some((t) => t.id === v)) return v as ThemeId;
  } catch {
    /* localStorage may be unavailable (SSR, private mode); fall through */
  }
  return DEFAULT_THEME;
}

function applyThemeToDom(theme: ThemeId): void {
  document.documentElement.setAttribute('data-theme', theme);
}

type ThemeContextValue = {
  theme: ThemeId;
  setTheme: (id: ThemeId) => void;
  themes: ThemeMeta[];
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    const t = readStoredTheme();
    // Apply synchronously so first paint already has the right colors.
    if (typeof document !== 'undefined') applyThemeToDom(t);
    return t;
  });

  useEffect(() => {
    applyThemeToDom(theme);
  }, [theme]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* persistence is best-effort */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, themes: THEMES }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

export function getThemeMeta(id: ThemeId): ThemeMeta {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}
